import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Drawer,
  Stack,
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  Skeleton,
  Divider,
  Pagination,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  Clock01Icon,
  ArrowRight01Icon,
  ShoppingBag01Icon,
  Invoice01Icon,
} from "@hugeicons/core-free-icons";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { db } from "@/firebase";
import { useAppSelector, useResponsive } from "@/hooks";
import { userSelector } from "@/features/auth/slice";
import routes from "@/constants/routes";
import { Order, OrderStatus } from "@/types";
import { sanitizePhone } from "@/utils/phones";
import { addCurrency } from "@/utils/numbers";
import { Currency } from "@/types/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  currentOrderId?: string;
  organizationId?: string;
  organizationAlias?: string;
  currency?: keyof typeof Currency;
}

const PAGE_SIZE = 10;

const ALLOWED_ACTIVE_STATUSES: OrderStatus[] = [
  OrderStatus.REVIEWING,
  OrderStatus.PENDING_PAYMENT,
  OrderStatus.PREPARING,
  OrderStatus.SENDING,
  OrderStatus.WAITING,
];

interface StatusDisplay {
  label: string;
  color: string;
  bg: string;
  border?: string;
}

interface OrderReference {
  id: string;
  timestamp: number;
}

const getStatusDisplay = (status?: OrderStatus | string): StatusDisplay => {
  switch (status) {
    case OrderStatus.REVIEWING:
      return { label: "En revisión", color: "#B45309", bg: "#FEF3C7", border: "#FDE68A" };
    case OrderStatus.PENDING_PAYMENT:
      return { label: "Esperando pago", color: "#B45309", bg: "#FEF3C7", border: "#FDE68A" };
    case OrderStatus.PREPARING:
      return { label: "En preparación", color: "#0369A1", bg: "#E0F2FE", border: "#BAE6FD" };
    case OrderStatus.SENDING:
      return { label: "En camino", color: "#4338CA", bg: "#EEF2FF", border: "#C7D2FE" };
    case OrderStatus.WAITING:
      return { label: "En espera", color: "#C2410C", bg: "#FFEDD5", border: "#FED7AA" };
    case OrderStatus.DELIVERED:
      return { label: "Entregado", color: "#047857", bg: "#D1FAE5", border: "#A7F3D0" };
    case OrderStatus.COMPLETED:
      return { label: "Completado", color: "#047857", bg: "#D1FAE5", border: "#A7F3D0" };
    case OrderStatus.REJECTED:
      return { label: "Rechazado", color: "#B91C1C", bg: "#FEE2E2", border: "#FECACA" };
    case OrderStatus.CANCELLED:
      return { label: "Cancelado", color: "#B91C1C", bg: "#FEE2E2", border: "#FECACA" };
    default:
      return { label: status || "Pendiente", color: "#475569", bg: "#F1F5F9", border: "#CBD5E1" };
  }
};

const getOrderTimestamp = (orderData: any): number => {
  if (!orderData) return 0;
  const val =
    orderData.orderedAt ||
    orderData.completedAt ||
    orderData.createdAt ||
    orderData.lastUpdatedAt ||
    0;

  if (typeof val === "number") return val;
  if (val && typeof val.toMillis === "function") return val.toMillis();
  if (val && typeof val.seconds === "number") return val.seconds * 1000;
  if (val instanceof Date) return val.getTime();
  return 0;
};

const toValidDate = (dateVal: any): Date | null => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
  if (typeof dateVal?.toDate === "function") return dateVal.toDate();
  if (typeof dateVal?.toMillis === "function") return new Date(dateVal.toMillis());
  if (typeof dateVal === "number") return new Date(dateVal);
  if (typeof dateVal === "string") {
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const OrderHistoryDrawer: React.FC<Props> = ({
  open,
  onClose,
  currentOrderId,
  organizationId,
  organizationAlias,
  currency = "NIO",
}) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const user = useAppSelector(userSelector);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [orderRefs, setOrderRefs] = useState<OrderReference[]>([]);
  const [ordersCache, setOrdersCache] = useState<Map<string, Order>>(new Map());
  const [currentPageOrders, setCurrentPageOrders] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingRefs, setIsLoadingRefs] = useState(false);
  const [isLoadingBatch, setIsLoadingBatch] = useState(false);

  // Fetch full details in batches of 10 orders for the specified page
  const fetchBatch = useCallback(
    async (
      pageNumber: number,
      allRefs: OrderReference[],
      cache: Map<string, Order>
    ) => {
      if (!organizationId || allRefs.length === 0) {
        setCurrentPageOrders([]);
        return;
      }

      setIsLoadingBatch(true);
      const startIndex = (pageNumber - 1) * PAGE_SIZE;
      const targetBatchRefs = allRefs.slice(startIndex, startIndex + PAGE_SIZE);

      const nextCache = new Map(cache);
      const missingRefs = targetBatchRefs.filter((ref) => !nextCache.has(ref.id));

      if (missingRefs.length > 0) {
        await Promise.all(
          missingRefs.map(async ({ id }) => {
            try {
              // 1. Check in active orders collection
              let orderDoc = await getDoc(
                doc(db, `organizations/${organizationId}/orders/${id}`)
              );
              if (orderDoc.exists()) {
                nextCache.set(id, { ...orderDoc.data(), id: orderDoc.id } as Order);
                return;
              }

              // 2. Check in completed orders collection
              orderDoc = await getDoc(
                doc(db, `organizations/${organizationId}/completedOrders/${id}`)
              );
              if (orderDoc.exists()) {
                nextCache.set(id, {
                  ...orderDoc.data(),
                  id: orderDoc.id,
                  status: orderDoc.data().status || OrderStatus.COMPLETED,
                } as Order);
                return;
              }

              // 3. Check in archived orders collection
              orderDoc = await getDoc(
                doc(db, `organizations/${organizationId}/archivedOrders/${id}`)
              );
              if (orderDoc.exists()) {
                nextCache.set(id, {
                  ...orderDoc.data(),
                  id: orderDoc.id,
                  status: orderDoc.data().status || OrderStatus.ARCHIVED,
                } as Order);
              }
            } catch (err) {
              console.error(`Error loading order ${id}:`, err);
            }
          })
        );
      }

      // Preserve the sorted order (newest to older) of the current page batch
      const pageOrders = targetBatchRefs
        .map((ref) => nextCache.get(ref.id))
        .filter(Boolean) as Order[];

      setOrdersCache(nextCache);
      setCurrentPageOrders(pageOrders);
      setIsLoadingBatch(false);
    },
    [organizationId]
  );

  // Initial discovery of all order references for the user/organization, sorted newest to older
  const fetchOrderReferences = useCallback(async () => {
    if (!organizationId || !user) return;
    setIsLoadingRefs(true);
    setCurrentPage(1);

    try {
      const refsMap = new Map<string, number>();
      const initialCache = new Map<string, Order>();

      // 1. Fetch referenced orders from users/${user.uid}/orders
      if (user.uid) {
        try {
          const userOrdersRef = collection(db, `users/${user.uid}/orders`);
          const userOrdersSnap = await getDocs(userOrdersRef);

          userOrdersSnap.forEach((docSnap) => {
            const data = docSnap.data();
            if (!data.organizationId || data.organizationId === organizationId) {
              const timestamp = getOrderTimestamp(data);
              refsMap.set(docSnap.id, timestamp);
            }
          });
        } catch (err) {
          console.error("Error fetching user order references:", err);
        }
      }

      // 2. Query orders matching the user phone number (active & completed)
      const cleanPhone = user.phone ? sanitizePhone(user.phone) : "";
      if (cleanPhone) {
        const phoneVariants = Array.from(
          new Set([cleanPhone, `+505${cleanPhone}`, user.phone].filter(Boolean))
        );

        // Active orders by phone
        try {
          const activeRef = collection(db, `organizations/${organizationId}/orders`);
          const qActive = query(activeRef, where("client.phone", "in", phoneVariants));
          const activeSnap = await getDocs(qActive);
          activeSnap.forEach((docSnap) => {
            const orderData = { ...docSnap.data(), id: docSnap.id } as Order;
            const timestamp = getOrderTimestamp(orderData);
            refsMap.set(docSnap.id, timestamp);
            initialCache.set(docSnap.id, orderData);
          });
        } catch (err) {
          console.warn("Active orders by phone search fallback:", err);
        }

        // Completed orders by phone
        try {
          const completedRef = collection(
            db,
            `organizations/${organizationId}/completedOrders`
          );
          const qCompleted = query(
            completedRef,
            where("client.phone", "in", phoneVariants)
          );
          const completedSnap = await getDocs(qCompleted);
          completedSnap.forEach((docSnap) => {
            const orderData = {
              ...docSnap.data(),
              id: docSnap.id,
              status: docSnap.data().status || OrderStatus.COMPLETED,
            } as Order;
            const timestamp = getOrderTimestamp(orderData);
            refsMap.set(docSnap.id, timestamp);
            initialCache.set(docSnap.id, orderData);
          });
        } catch (err) {
          console.warn("Completed orders by phone search fallback:", err);
        }
      }

      // Sort order references from newest to older
      const sortedRefs: OrderReference[] = Array.from(refsMap.entries())
        .map(([id, timestamp]) => ({ id, timestamp }))
        .sort((a, b) => b.timestamp - a.timestamp);

      setOrderRefs(sortedRefs);
      setOrdersCache(initialCache);

      // Fetch the first batch (10 orders)
      await fetchBatch(1, sortedRefs, initialCache);
    } catch (error) {
      console.error("Error fetching order history:", error);
    } finally {
      setIsLoadingRefs(false);
    }
  }, [organizationId, user, fetchBatch]);

  useEffect(() => {
    if (open) {
      fetchOrderReferences();
    }
  }, [open, fetchOrderReferences]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    if (newPage === currentPage) return;
    setCurrentPage(newPage);
    fetchBatch(newPage, orderRefs, ordersCache);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleOrderClick = (order: Order) => {
    onClose();

    if (order.id === currentOrderId) {
      return;
    }

    const isActive = ALLOWED_ACTIVE_STATUSES.includes(order.status as OrderStatus);
    const isCompleted =
      order.status === OrderStatus.COMPLETED ||
      (order.status as string) === OrderStatus.DELIVERED;

    const targetUrl = organizationAlias
      ? routes.organizations.order.status
        .replace(":organizationAlias", organizationAlias)
        .replace(":orderId", order.id)
      : routes.orderStatus.replace(":orderId", order.id);

    if (isActive) {
      navigate(targetUrl, { state: { order, view: "status" } });
    } else if (isCompleted) {
      navigate(targetUrl, { state: { order, view: "thanks" } });
    } else {
      navigate(targetUrl, { state: { order, view: "thanks" } });
    }
  };

  const totalOrders = orderRefs.length;
  const totalPages = Math.ceil(totalOrders / PAGE_SIZE);
  const startIdx = totalOrders > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endIdx = Math.min(currentPage * PAGE_SIZE, totalOrders);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 430,
          maxHeight: isMobile ? "85vh" : "100vh",
          p: 0,
          bgcolor: "#F4F7FE",
          borderTopLeftRadius: isMobile ? 24 : 0,
          borderTopRightRadius: isMobile ? 24 : 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        p={2.5}
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#EFEFEF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HugeiconsIcon icon={Invoice01Icon} size={22} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Historial de Pedidos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tus pedidos en este comercio
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <HugeiconsIcon icon={Cancel01Icon} size={20} />
        </IconButton>
      </Stack>

      {/* Orders List Content */}
      <Box ref={scrollContainerRef} flex={1} overflow="auto" p={2.5}>
        {isLoadingRefs || isLoadingBatch ? (
          <Stack spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                variant="rounded"
                height={115}
                sx={{ borderRadius: 3 }}
              />
            ))}
          </Stack>
        ) : totalOrders === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            py={8}
            px={3}
            textAlign="center"
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <HugeiconsIcon icon={ShoppingBag01Icon} size={36} />
            </Box>
            <Typography variant="h4" fontWeight={600}>
              No tienes pedidos aún
            </Typography>
            <Typography variant="body2" color="text.secondary" maxWidth={280}>
              Los pedidos que realices con tu cuenta aparecerán aquí para que puedas
              darles seguimiento.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {currentPageOrders.map((order) => {
              const statusDisplay = getStatusDisplay(order.status);
              const isCurrent = order.id === currentOrderId;
              const isActive = ALLOWED_ACTIVE_STATUSES.includes(
                order.status as OrderStatus
              );
              const orderDate =
                order.orderedAt || order.completedAt || order.createdAt;
              const validDate = toValidDate(orderDate);

              return (
                <Paper
                  key={order.id}
                  elevation={0}
                  onClick={() => handleOrderClick(order)}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1.5px solid",
                    borderColor: isCurrent ? "primary.main" : "#E2E8F0",
                    boxShadow: isCurrent
                      ? "0px 4px 14px rgba(1, 95, 238, 0.12)"
                      : "0px 2px 6px rgba(0, 0, 0, 0.02)",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0px 6px 18px rgba(0, 0, 0, 0.08)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Stack spacing={1.2}>
                    {/* Top Row: Order Number & Status */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Pedido #{order.number || order.id.slice(-6)}
                        </Typography>
                        {isCurrent && (
                          <Chip
                            label="Actual"
                            size="small"
                            color="primary"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Stack>

                      <Chip
                        label={statusDisplay.label}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: statusDisplay.color,
                          bgcolor: statusDisplay.bg,
                          border: statusDisplay.border
                            ? `1px solid ${statusDisplay.border}`
                            : "none",
                        }}
                      />
                    </Stack>

                    {/* Middle: Date & Items count */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      color="text.secondary"
                    >
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <HugeiconsIcon icon={Clock01Icon} size={15} />
                        <Typography variant="caption">
                          {validDate
                            ? format(validDate, "d 'de' MMM, yyyy • h:mm a", {
                              locale: es,
                            })
                            : "Fecha desconocida"}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" fontWeight={500}>
                        {order.items?.length || 0}{" "}
                        {order.items?.length === 1 ? "producto" : "productos"}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Bottom Row: Total & Action Hint */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Total:
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {order.total != null
                            ? addCurrency(order.total, currency)
                            : "—"}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                          color: isActive ? "primary.main" : "text.secondary",
                          fontWeight: 600,
                          fontSize: "0.78rem",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="inherit"
                        >
                          {isActive ? "Ver estatus" : "Ver detalle"}
                        </Typography>
                        <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Pagination Footer */}
      {!isLoadingRefs && totalPages > 1 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "#fff",
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Mostrando {startIdx}–{endIdx} de {totalOrders} pedidos
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size={isMobile ? "small" : "medium"}
            showFirstButton={totalPages > 3}
            showLastButton={totalPages > 3}
            disabled={isLoadingBatch}
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 600,
                borderRadius: 2,
              },
            }}
          />
        </Paper>
      )}
    </Drawer>
  );
};

export default OrderHistoryDrawer;
